import { useState } from 'react';
import { clsx } from '../../../../ds/clsx';
import { Banner, Skeleton, Toggle, useToast } from '../../../../ds/components';
import { errorMessage } from '../../../../lib/api';
import { OrganizationPage, QueryError } from '../users/OrganizationPage';
import { useModules, useToggleModule, type ModuleCard } from './api';
import styles from './ModulesPage.module.css';

const INTRO =
  'Ausgeschaltete Module verschwinden nicht aus dem Tools-Menü, sondern zeigen dort „kein Zugriff“. Daten bleiben erhalten.';

/**
 * Admin 05 „Module": seven cards (PKV last, PKV M0) (name, status Aktiv/Aus, description, note, switch) → `PATCH /modules/:key`.
 * A refusal of the server (409) stays in its card; every change refreshes the session (tools, modules).
 */
export function ModulesPage() {
  const modules = useModules();
  const data = modules.data;

  return (
    <OrganizationPage variant="admin" title="Module" description={data?.intro ?? INTRO}>
      {modules.isError ? <QueryError error={modules.error} /> : null}
      <div className={styles.grid}>
        {modules.isPending
          ? [0, 1, 2, 3, 4, 5, 6].map((index) => (
              <div key={index} className={styles.skeletonCard} aria-hidden="true">
                <Skeleton width="45%" height={14} />
                <Skeleton width="85%" />
              </div>
            ))
          : null}
        {data?.items.map((card) => (
          <ModuleCardView key={card.key} card={card} />
        ))}
      </div>
    </OrganizationPage>
  );
}

function ModuleCardView({ card }: { card: ModuleCard }) {
  const { toast } = useToast();
  const toggle = useToggleModule();
  const [error, setError] = useState<string | null>(null);
  const titleId = `module-${card.key}`;

  function change(active: boolean) {
    setError(null);
    toggle.mutate(
      { key: card.key, active },
      {
        onSuccess: () =>
          toast({ message: active ? `${card.name} ist eingeschaltet.` : `${card.name} ist ausgeschaltet.` }),
        onError: (failure) => setError(errorMessage(failure)),
      },
    );
  }

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <div className={styles.cardRow}>
        <div className={styles.cardText}>
          <div className={styles.nameRow}>
            <h3 id={titleId} className={styles.name}>
              {card.name}
            </h3>
            <span className={clsx(styles.status, card.active ? styles.statusOn : styles.statusOff)}>
              {card.active ? 'Aktiv' : 'Aus'}
            </span>
          </div>
          <p className={styles.description}>{card.description}</p>
          {card.note ? <p className={styles.note}>{card.note}</p> : null}
        </div>
        <Toggle
          size="xlarge"
          label={card.name}
          labelHidden
          checked={card.active}
          disabled={!card.actions.toggle.allowed || toggle.isPending}
          title={card.actions.toggle.reason ?? undefined}
          onChange={change}
        />
      </div>
      {error ? (
        <Banner tone="danger" className={styles.cardBanner}>
          {error}
        </Banner>
      ) : null}
    </section>
  );
}
